import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useNavigate, useSearchParams } from 'react-router'
import { api } from '../api/client.js'
import { useApi } from '../api/useApi.js'
import { useAuth } from '../auth.jsx'
import { pickI18n } from '../i18n'
import { money } from '../lib/money.js'
import { Loading } from './states.jsx'

const MEAL_CHOICES = [1, 3, 5, 10, 30]

function Chip({ active, onClick, children }) {
  return (
    <button
      type="button"
      aria-pressed={active}
      onClick={onClick}
      className={`cursor-pointer rounded-2xl px-4 py-3 text-left transition ${
        active ? 'bg-ink text-on-ink' : 'border-[1.5px] border-edge bg-surface text-ink hover:border-ink'
      }`}
    >
      {children}
    </button>
  )
}

/** One-off "N meals" or a monthly donation, for one feeder or the general fund. */
export default function DonatePage() {
  const { t, i18n } = useTranslation()
  const lang = i18n.resolvedLanguage
  const { user } = useAuth()
  const navigate = useNavigate()
  const [params] = useSearchParams()
  const config = useApi(() => api.donationConfig())
  const feeders = useApi(() => api.feeders())
  const [kind, setKind] = useState(params.get('kind') === 'monthly' ? 'monthly' : 'one_off')
  const [feeder, setFeeder] = useState(params.get('feeder') ?? '')
  const [meals, setMeals] = useState(3)
  const [monthly, setMonthly] = useState(null)
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [state, setState] = useState({ busy: false, error: null })

  if (config.loading) return <Loading />
  const c = config.data
  if (!c?.enabled) {
    return (
      <main className="mx-auto flex max-w-xl flex-col items-center gap-4 px-4 py-24 text-center sm:px-8">
        <span className="text-6xl">🥣</span>
        <h1 className="text-3xl font-black">{t('donate.soonTitle')}</h1>
        <p className="text-lg text-muted">{t('donate.soonText')}</p>
        <Link to="/transparency" className="font-extrabold text-teal no-underline">{t('donate.transparency')} →</Link>
      </main>
    )
  }
  const monthlyCents = monthly ?? c.monthly_presets_cents[1] ?? c.monthly_presets_cents[0]
  const amount = kind === 'one_off' ? meals * c.meal_price_cents : monthlyCents
  const fmt = (cents) => money(cents, c.currency, lang)

  const pay = async () => {
    if (kind === 'monthly' && !user) return navigate('/login', { state: { from: `/donate?kind=monthly&feeder=${feeder}` } })
    setState({ busy: true, error: null })
    try {
      const { url } = await api.checkout({
        kind,
        feeder_slug: feeder || null,
        meals: kind === 'one_off' ? meals : null,
        amount_cents: kind === 'monthly' ? monthlyCents : null,
        public_name: name.trim() || null,
        email: !user && email.trim() ? email.trim() : null,
        locale: lang,
      })
      window.location.assign(url)
    } catch (e) {
      setState({ busy: false, error: e.message })
    }
  }

  return (
    <main className="mx-auto flex max-w-3xl flex-col gap-7 px-4 pt-10 pb-[72px] sm:px-8">
      <div className="flex flex-col gap-2">
        <h1 className="text-[clamp(32px,4vw,44px)] font-black tracking-[-1px]">{t('donate.title')}</h1>
        <p className="text-lg text-muted">{t('donate.lead')}</p>
      </div>

      <div className="flex gap-2" role="tablist">
        {['one_off', 'monthly'].map((k) => (
          <button
            key={k}
            type="button"
            role="tab"
            aria-selected={kind === k}
            onClick={() => setKind(k)}
            className={`cursor-pointer rounded-full px-5 py-2.5 font-extrabold ${kind === k ? 'bg-ink text-on-ink' : 'border-[1.5px] border-edge text-ink'}`}
          >
            {t(`donate.kind.${k}`)}
          </button>
        ))}
      </div>

      <section className="flex flex-col gap-3">
        <h2 className="text-sm font-extrabold tracking-[1.2px] text-subtle uppercase">{t('donate.howMuch')}</h2>
        <div className="grid grid-cols-[repeat(auto-fill,minmax(130px,1fr))] gap-2.5">
          {kind === 'one_off'
            ? MEAL_CHOICES.map((n) => (
                <Chip key={n} active={meals === n} onClick={() => setMeals(n)}>
                  <span className="block text-lg font-black">{t('donate.meals', { count: n })}</span>
                  <span className="text-sm font-bold opacity-80">{fmt(n * c.meal_price_cents)}</span>
                </Chip>
              ))
            : c.monthly_presets_cents.map((cents) => (
                <Chip key={cents} active={monthlyCents === cents} onClick={() => setMonthly(cents)}>
                  <span className="block text-lg font-black">{t('donate.perMonth', { amount: fmt(cents) })}</span>
                  <span className="text-sm font-bold opacity-80">
                    ≈ {t('donate.meals', { count: Math.floor(cents / c.meal_price_cents) })}
                  </span>
                </Chip>
              ))}
        </div>
        <p className="m-0 text-sm text-subtle">{t('donate.mealPrice', { price: fmt(c.meal_price_cents) })}</p>
      </section>

      <section className="flex flex-col gap-3">
        <h2 className="text-sm font-extrabold tracking-[1.2px] text-subtle uppercase">{t('donate.where')}</h2>
        <select
          value={feeder}
          onChange={(e) => setFeeder(e.target.value)}
          className="h-[50px] rounded-xl border-[1.5px] border-edge bg-surface px-4 text-base font-bold text-ink"
        >
          <option value="">{t('donate.generalFund')}</option>
          {(feeders.data ?? []).map((f) => (
            <option key={f.slug} value={f.slug}>
              {pickI18n(f.name, lang)}
            </option>
          ))}
        </select>
      </section>

      <section className="grid gap-3 sm:grid-cols-2">
        <label className="flex flex-col gap-1.5 text-sm font-extrabold">
          {t('donate.publicName')}
          <input
            maxLength={60}
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={t('donate.anonymous')}
            className="h-[50px] rounded-xl border-[1.5px] border-edge bg-surface px-4 text-base font-medium text-ink outline-none focus:border-teal"
          />
        </label>
        {!user ? (
          <label className="flex flex-col gap-1.5 text-sm font-extrabold">
            {t('donate.email')}
            <input
              type="email"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              className="h-[50px] rounded-xl border-[1.5px] border-edge bg-surface px-4 text-base font-medium text-ink outline-none focus:border-teal"
            />
          </label>
        ) : null}
      </section>

      {state.error ? <p role="alert" className="m-0 font-bold text-live">{state.error}</p> : null}
      <button
        type="button"
        onClick={pay}
        disabled={state.busy}
        className="h-[58px] cursor-pointer rounded-2xl bg-teal text-lg font-extrabold text-on-teal transition hover:bg-teal-dark disabled:opacity-60"
      >
        {kind === 'monthly' && !user
          ? t('donate.loginForMonthly')
          : t(kind === 'monthly' ? 'donate.payMonthly' : 'donate.pay', { amount: fmt(amount) })}
      </button>
      <p className="m-0 text-center text-sm text-subtle">
        {t('donate.secure')} ·{' '}
        <Link to="/transparency" className="font-bold text-teal no-underline">{t('donate.transparency')}</Link>
      </p>
    </main>
  )
}
